import React, { useState, useCallback, useEffect, useMemo, useImperativeHandle, forwardRef } from "react"
import { Box, VStack, Text, Skeleton, HStack, Dialog as ChakraDialog } from "@chakra-ui/react"
import { Alert } from "@hydrologiq/shared/chakra/Alert"
import AssetAvailabilityCalendarSimple from "./AssetAvailabilityCalendar"
import { AssetAvailabilityCalendarDocument } from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { addDays, subDays, format as formatDate, isValid, parseISO } from "date-fns"
import {
  useOrderDates,
  useOrderWorkflowActions,
  useSelectedAssets,
  useMultipleAssets,
} from "@hydrologiq/shared/contexts/OrderWorkflowContext"
import { CancelButton } from "@hydrologiq/shared/content/components/Button"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"

interface AssetItem {
  id: string
  name: string
}

interface AssetAvailabilityCalendarWithDataProps {
  onBack?: () => void
}

export interface AssetAvailabilityCalendarHandle {
  handleBackClick: () => void
}

/**
 * Calendar component that owns its data fetching and reads directly from context.
 * This eliminates prop-passing chains that cause unnecessary re-renders.
 */
const AssetAvailabilityCalendarWithDataInner = forwardRef<
  AssetAvailabilityCalendarHandle,
  AssetAvailabilityCalendarWithDataProps
>(({ onBack }, ref) => {
  // Get initial dates from context (only used for initialization)
  const contextDates = useOrderDates()
  const selectedAssets = useSelectedAssets()
  const multipleAssets = useMultipleAssets()
  const { updateOrderDates, addAsset, removeAsset } = useOrderWorkflowActions()

  // Manage dates as LOCAL state - don't update context until saved
  // This prevents re-renders when user changes dates in the calendar
  const [localDates, setLocalDates] = useState({
    start: contextDates.start,
    end: contextDates.end,
  })

  // Track pending asset changes (local state before saving)
  const [localSelectedAssets, setLocalSelectedAssets] = useState<typeof selectedAssets>(selectedAssets || [])

  // Track if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    // Check if dates changed
    const datesChanged = localDates.start !== contextDates.start || localDates.end !== contextDates.end

    // Check if assets changed (compare IDs)
    const contextAssetIds = (selectedAssets || [])
      .map((a) => a.id)
      .sort()
      .join(",")
    const localAssetIds = localSelectedAssets
      .map((a) => a.id)
      .sort()
      .join(",")
    const assetsChanged = contextAssetIds !== localAssetIds

    return datesChanged || assetsChanged
  }, [localDates, contextDates, selectedAssets, localSelectedAssets])

  // Sync with context dates on mount or when context dates change externally
  // (e.g., when user changes dates in the order form)
  useEffect(() => {
    if (contextDates.start && contextDates.end) {
      // Auto-correct if end date is before start date
      const startDate = new Date(contextDates.start)
      const endDate = new Date(contextDates.end)

      if (endDate < startDate) {
        // Set end date to one day after start date
        const correctedEndDate = formatDate(addDays(startDate, 1), "yyyy-MM-dd")
        setLocalDates({
          start: contextDates.start,
          end: correctedEndDate,
        })
      } else {
        setLocalDates({
          start: contextDates.start,
          end: contextDates.end,
        })
      }
    }
  }, [contextDates.start, contextDates.end])

  // Sync local assets with context assets when they change externally
  useEffect(() => {
    setLocalSelectedAssets(selectedAssets || [])
  }, [selectedAssets])

  const orderStartDate = localDates.start
  const orderEndDate = localDates.end

  // Track if we've ever loaded data (to distinguish initial load from refetch)
  const hasLoadedDataRef = React.useRef(false)

  // Keep a stable reference to the last valid data
  const lastValidDataRef = React.useRef<any>(null)

  // State for calendar buffer (weeks before/after order dates)
  const [calendarBuffer, setCalendarBuffer] = useState(4)

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // Calculate query dates based on order dates + buffer
  const getQueryDates = useCallback(
    (buffer: number) => {
      // Parse dates safely
      const orderStart = parseISO(orderStartDate)
      const orderEnd = parseISO(orderEndDate)

      // Validate dates
      if (!isValid(orderStart) || !isValid(orderEnd)) {
        console.error("Invalid order dates:", { orderStartDate, orderEndDate })
        // Return current date as fallback
        const now = new Date()
        return {
          start: formatDate(now, "yyyy-MM-dd"),
          end: formatDate(addDays(now, 14), "yyyy-MM-dd"),
        }
      }

      // Check if end date is before start date
      if (orderEnd < orderStart) {
        console.error("End date is before start date:", { orderStartDate, orderEndDate })
        // Return null to skip query
        return null
      }

      // Add buffer weeks on each side using date-fns
      const queryStart = subDays(orderStart, buffer * 7)
      const queryEnd = addDays(orderEnd, buffer * 7)

      return {
        start: formatDate(queryStart, "yyyy-MM-dd"),
        end: formatDate(queryEnd, "yyyy-MM-dd"),
      }
    },
    [orderStartDate, orderEndDate]
  )

  const queryDates = getQueryDates(calendarBuffer)

  // Store previous query dates to detect changes
  const [previousQueryDates, setPreviousQueryDates] = React.useState(queryDates)

  const { data, loading, error, refetch } = useQueryWithEndpoint(
    AssetAvailabilityCalendarDocument,
    {
      variables: {
        start: queryDates?.start || "",
        end: queryDates?.end || "",
        page: 1,
        per_page: 100, // Load more assets for better visualization
      },
      skip: !queryDates, // Skip query if dates are invalid
      fetchPolicy: "cache-and-network", // Show cached data immediately while fetching new data
      notifyOnNetworkStatusChange: true, // Ensure loading state updates during refetch
    },
    "primary"
  )

  // Update refs when we get data
  useEffect(() => {
    if (data?.calendar) {
      hasLoadedDataRef.current = true
      lastValidDataRef.current = data.calendar
    }
  }, [data])

  // Initial load = loading AND we've never loaded data before
  const isInitialLoad = loading && !hasLoadedDataRef.current

  // Refetching = loading AND we've already loaded data before
  const isRefetching = loading && hasLoadedDataRef.current

  // Use current data if available, otherwise fall back to last valid data
  // CRITICAL: Memoize to prevent creating new object reference on every render
  // ALWAYS show last valid data if we have it, even during initial load of new date range
  const displayData = useMemo(() => {
    if (data?.calendar) {
      return data.calendar
    }
    // If loading and we have previous data, show it
    if (loading && lastValidDataRef.current) {
      return lastValidDataRef.current
    }
    return null
  }, [data?.calendar, loading])

  // Auto-refetch when query dates change
  useEffect(() => {
    if (!queryDates || !previousQueryDates) return

    if (previousQueryDates.start !== queryDates.start || previousQueryDates.end !== queryDates.end) {
      setPreviousQueryDates(queryDates)
      refetch({
        start: queryDates.start,
        end: queryDates.end,
        page: 1,
        per_page: 100,
      })
    }
  }, [queryDates, previousQueryDates, refetch])

  // Handle order date changes from calendar component
  // Update LOCAL state only - don't update context until user clicks "Save Changes"
  // isEditing=true: User is typing, update local state but don't refetch
  // isEditing=false: Not used in booking mode (refresh button triggers refetch via onRefresh)
  const handleOrderDateChange = useCallback((newStartDate: string, newEndDate: string, isEditing?: boolean) => {
    // Always update local dates for unsaved changes detection
    setLocalDates({
      start: newStartDate,
      end: newEndDate,
    })
    // Note: Refetch is triggered by the refresh button calling onRefresh, not by date changes
  }, [])

  // Handle calendar buffer changes from calendar component
  const handleCalendarBufferChange = useCallback((newBuffer: number) => {
    setCalendarBuffer(newBuffer)
    // The useEffect will detect queryDates change and auto-refetch
  }, [])

  // Handle asset selection - add asset to list and update dates in context, then navigate back
  // Handle asset selection - update local state only
  const handleAssetSelect = useCallback((asset: AssetItem, orderStart: string, orderEnd: string) => {
    // Add asset to local list (not context yet)
    setLocalSelectedAssets((prev) => {
      // Check if already selected
      if (prev.some((a) => a.id === asset.id)) {
        return prev
      }
      return [...prev, asset]
    })

    // Update local dates if they changed
    setLocalDates({
      start: orderStart,
      end: orderEnd,
    })
  }, [])

  // Handle asset removal from calendar - update local state only
  const handleAssetRemove = useCallback((assetId: string) => {
    console.log(localSelectedAssets.filter((a) => a.id !== assetId))
    setLocalSelectedAssets((prev) => prev.filter((a) => a.id !== assetId))
  }, [])

  // Save all changes (dates + assets) to context
  const handleSaveChanges = useCallback(() => {
    // Update dates
    updateOrderDates(localDates.start, localDates.end)

    // Sync assets: add new ones, remove old ones
    const contextAssetIds = new Set((selectedAssets || []).map((a) => a.id))
    const localAssetIds = new Set(localSelectedAssets.map((a) => a.id))

    // Remove assets that are no longer selected
    selectedAssets?.forEach((asset) => {
      if (!localAssetIds.has(asset.id)) {
        removeAsset(asset.id)
      }
    })

    // Add new assets
    localSelectedAssets.forEach((asset) => {
      if (!contextAssetIds.has(asset.id)) {
        addAsset(asset)
      }
    })

    // Navigate back after saving
    if (onBack) {
      onBack()
    }
  }, [localDates, localSelectedAssets, selectedAssets, updateOrderDates, addAsset, removeAsset, onBack])

  // Reset changes without navigating back
  const handleDiscardChanges = useCallback(() => {
    // Reset local state to context state
    setLocalDates({ start: contextDates.start, end: contextDates.end })
    setLocalSelectedAssets(selectedAssets || [])
    // Note: Does NOT navigate back - user can continue editing or use back button
  }, [contextDates, selectedAssets])

  // Handle back button click with unsaved changes check
  const handleBackClick = useCallback(() => {
    if (hasUnsavedChanges) {
      // Show confirmation dialog
      setShowConfirmDialog(true)
    } else {
      // No unsaved changes, navigate back directly
      if (onBack) {
        onBack()
      }
    }
  }, [hasUnsavedChanges, onBack])

  // Handle save and exit from confirmation dialog
  const handleSaveAndExit = useCallback(() => {
    setShowConfirmDialog(false)
    handleSaveChanges()
  }, [handleSaveChanges])

  // Handle exit without saving from confirmation dialog
  const handleExitWithoutSaving = useCallback(() => {
    setShowConfirmDialog(false)
    // Reset changes to context state
    setLocalDates({ start: contextDates.start, end: contextDates.end })
    setLocalSelectedAssets(selectedAssets || [])
    // Navigate back
    if (onBack) {
      onBack()
    }
  }, [contextDates, selectedAssets, onBack])

  // Handle cancel from confirmation dialog
  const handleCancelExit = useCallback(() => {
    setShowConfirmDialog(false)
  }, [])

  // Expose handleBackClick to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      handleBackClick,
    }),
    [handleBackClick]
  )

  // Skeleton loader for initial load
  const CalendarSkeleton = () => (
    <VStack gap={4} width="100%" height="100%" align="stretch" p={4}>
      {/* Filter controls skeleton */}
      <HStack gap={3} width="100%">
        <Skeleton height="40px" width="160px" />
        <Skeleton height="40px" width="160px" />
        <Box flex={1} />
        <Skeleton height="40px" width="300px" />
      </HStack>

      {/* Multi-select skeleton */}
      <Skeleton height="40px" width="425px" />

      {/* Info banner skeleton */}
      <Skeleton height="50px" width="100%" />

      {/* Timeline skeleton */}
      <Box borderWidth="1px" borderRadius="md" p={4} flex={1}>
        <VStack gap={3} align="stretch">
          <Skeleton height="40px" width="100%" />
          <Skeleton height="60px" width="100%" />
          <Skeleton height="60px" width="100%" />
          <Skeleton height="60px" width="100%" />
          <Skeleton height="60px" width="100%" />
        </VStack>
      </Box>
    </VStack>
  )

  // Validation checks - after all hooks are called
  // Don't render if we don't have valid dates
  if (!orderStartDate || !orderEndDate) {
    return (
      <VStack height="100%" justify="center" align="center" p={8}>
        <Text color="gray.500" fontSize="lg">
          Please select start and end dates in the order form first.
        </Text>
      </VStack>
    )
  }

  // Show skeleton only on very first load
  if (isInitialLoad && !displayData) {
    return (
      <Box width="100%" height="100%">
        <CalendarSkeleton />
      </Box>
    )
  }

  // Show error only if no data available
  if (error && !displayData) {
    return (
      <Box width="100%" height="100%">
        <Alert status="error" title="Error loading calendar">
          {error.message}
        </Alert>
      </Box>
    )
  }

  // Show empty state
  if (!loading && !error && displayData?.data?.length === 0) {
    return (
      <Box width="100%" height="100%">
        <VStack height="100%" justify="center" align="center">
          <Text color="gray.500">No assets found for the selected date range.</Text>
        </VStack>
      </Box>
    )
  }

  // Once we have data, ALWAYS render the calendar (even during refetch)
  // This prevents unmounting/remounting which causes flickering
  if (displayData) {
    return (
      <>
        <Box width="100%" height="100%">
          <AssetAvailabilityCalendarSimple
            data={displayData as any}
            startDate={orderStartDate}
            endDate={orderEndDate}
            isRefreshing={isRefetching}
            onOrderDateChange={handleOrderDateChange}
            onCalendarBufferChange={handleCalendarBufferChange}
            onAssetSelect={handleAssetSelect}
            disableStatusFilter={true}
            hasUnsavedChanges={hasUnsavedChanges}
            onSaveChanges={handleSaveChanges}
            onDiscardChanges={handleDiscardChanges}
            selectedAssetIds={localSelectedAssets}
            onRemoveAsset={handleAssetRemove}
            multipleAssets={multipleAssets}
          />
        </Box>

        {/* Confirmation dialog for unsaved changes */}
        <ChakraDialog.Root
          placement={"center"}
          open={showConfirmDialog}
          onOpenChange={(details) => !details.open && handleCancelExit()}
        >
          <ChakraDialog.Backdrop />
          <ChakraDialog.Positioner>
            <ChakraDialog.Content maxWidth="lg">
              <ChakraDialog.Header>
                <ChakraDialog.Title>Unsaved Changes</ChakraDialog.Title>
                <ChakraDialog.CloseTrigger onClick={handleCancelExit} />
              </ChakraDialog.Header>

              <ChakraDialog.Body>
                <VStack gap={3} align="stretch">
                  <Text>You have unsaved changes. What would you like to do?</Text>
                </VStack>
              </ChakraDialog.Body>

              <ChakraDialog.Footer>
                <HStack gap={3} justifyContent="flex-end">
                  <CancelButton onClick={handleCancelExit}>Go Back</CancelButton>
                  <ActionButton
                    context={{ label: "Exit Without Saving", description: "Discard changes", variant: "secondary" }}
                    onClick={handleExitWithoutSaving}
                  />
                  <ActionButton
                    context={{ label: "Save and Exit", description: "Save changes and exit", variant: "primary" }}
                    onClick={handleSaveAndExit}
                  />
                </HStack>
              </ChakraDialog.Footer>
            </ChakraDialog.Content>
          </ChakraDialog.Positioner>
        </ChakraDialog.Root>
      </>
    )
  }

  // Fallback (should never reach here)
  return null
})

AssetAvailabilityCalendarWithDataInner.displayName = "AssetAvailabilityCalendarWithData"

export default AssetAvailabilityCalendarWithDataInner
